import { z } from 'zod';

/** Preset tokens. Custom uses the same `{amount}{unit}` shape, for example 90m. */
export const SESSION_TIMEOUT_PRESET_VALUES = [
  '1h',
  '4h',
  '12h',
  '24h',
  '3d',
  '7d',
  '30d',
] as const;
export const SESSION_TIMEOUT_NEVER = 'never' as const;
export const SESSION_TIMEOUT_VALUES = [
  ...SESSION_TIMEOUT_PRESET_VALUES,
  SESSION_TIMEOUT_NEVER,
] as const;
export const SESSION_TIMEOUT_DEFAULT = '30d' as const;

export const MIN_SESSION_TIMEOUT_MINUTES = 30;
/** 30 days of 24 hours, matching the existing 30d preset. */
export const MAX_SESSION_TIMEOUT_MINUTES = 30 * 24 * 60;

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

const PRESET_MINUTES: Record<
  (typeof SESSION_TIMEOUT_PRESET_VALUES)[number],
  number
> = {
  '1h': MINUTES_PER_HOUR,
  '4h': 4 * MINUTES_PER_HOUR,
  '12h': 12 * MINUTES_PER_HOUR,
  '24h': 24 * MINUTES_PER_HOUR,
  '3d': 3 * MINUTES_PER_DAY,
  '7d': 7 * MINUTES_PER_DAY,
  '30d': 30 * MINUTES_PER_DAY,
};

export type SessionTimeoutPreset = (typeof SESSION_TIMEOUT_VALUES)[number];
/** Stored token: a preset, `never`, or a custom value such as `90m`. */
export type SessionTimeout = string;
export type SessionTimeoutUnit = 'minutes' | 'hours' | 'days';
export type SessionTimeoutErrorType =
  'empty' | 'not_whole' | 'below_min' | 'above_max';

export const SESSION_TIMEOUT_OPTION_LABELS: Record<
  SessionTimeoutPreset,
  string
> = {
  '1h': '1 hour',
  '4h': '4 hours',
  '12h': '12 hours',
  '24h': '24 hours',
  '3d': '3 days',
  '7d': '7 days',
  '30d': '30 days',
  never: 'Never',
};

export type ParsedSessionTimeout =
  | {
      kind: 'never';
      token: 'never';
      minutes: null;
      amount: null;
      unit: null;
      isCustom: false;
    }
  | {
      kind: 'preset' | 'custom';
      token: string;
      minutes: number;
      amount: number;
      unit: SessionTimeoutUnit;
      isCustom: boolean;
    };

/**
 * `90m` is custom and means 90 minutes. `2h` is 2 hours. `14d` is 14 days.
 * A duration that matches a preset keeps that preset, so `240m` becomes `4h`
 * and 24 hours stays `24h` instead of `1d`.
 * Returns null when the value is not a whole duration from 30 minutes through 30 days.
 */
export function canonicalizeSessionTimeout(value: string): string | null {
  if (value === SESSION_TIMEOUT_NEVER) return SESSION_TIMEOUT_NEVER;

  const match = /^(\d+)(m|h|d)$/.exec(value);
  if (!match) return null;

  const amount = Number(match[1]);
  const suffix = match[2] as 'm' | 'h' | 'd';
  if (!Number.isSafeInteger(amount) || amount <= 0) return null;

  const factor =
    suffix === 'm' ? 1 : suffix === 'h' ? MINUTES_PER_HOUR : MINUTES_PER_DAY;
  if (amount > Math.floor(Number.MAX_SAFE_INTEGER / factor)) return null;

  const minutes = amount * factor;
  if (
    minutes < MIN_SESSION_TIMEOUT_MINUTES ||
    minutes > MAX_SESSION_TIMEOUT_MINUTES
  )
    return null;

  return minutesToCanonicalToken(minutes);
}

export function minutesToCanonicalToken(minutes: number): string {
  for (const preset of SESSION_TIMEOUT_PRESET_VALUES) {
    if (PRESET_MINUTES[preset] === minutes) return preset;
  }
  if (minutes % MINUTES_PER_DAY === 0) return `${minutes / MINUTES_PER_DAY}d`;
  if (minutes % MINUTES_PER_HOUR === 0) return `${minutes / MINUTES_PER_HOUR}h`;
  return `${minutes}m`;
}

/** Missing or invalid values fall back to 30 days, the new institution default. */
export function resolveSessionTimeout(
  value: string | null | undefined
): string {
  if (value == null || value === '') return SESSION_TIMEOUT_DEFAULT;
  return canonicalizeSessionTimeout(value) ?? SESSION_TIMEOUT_DEFAULT;
}

export function parseSessionTimeout(
  value: string | null | undefined
): ParsedSessionTimeout | null {
  if (value == null || value === '') return null;
  const token = canonicalizeSessionTimeout(value);
  if (!token) return null;
  if (token === SESSION_TIMEOUT_NEVER) {
    return {
      kind: 'never',
      token: 'never',
      minutes: null,
      amount: null,
      unit: null,
      isCustom: false,
    };
  }

  const match = /^(\d+)(m|h|d)$/.exec(token);
  if (!match) return null;
  const amount = Number(match[1]);
  const unit: SessionTimeoutUnit =
    match[2] === 'm' ? 'minutes' : match[2] === 'h' ? 'hours' : 'days';
  const isPreset = (
    SESSION_TIMEOUT_PRESET_VALUES as readonly string[]
  ).includes(token);
  const minutes =
    unit === 'minutes'
      ? amount
      : unit === 'hours'
        ? amount * MINUTES_PER_HOUR
        : amount * MINUTES_PER_DAY;

  return {
    kind: isPreset ? 'preset' : 'custom',
    token,
    minutes,
    amount,
    unit,
    isCustom: !isPreset,
  };
}

export function formatSessionTimeoutLabel(value: string): string | null {
  const parsed = parseSessionTimeout(value);
  if (!parsed || parsed.kind === 'never') return null;
  if (parsed.kind === 'preset')
    return SESSION_TIMEOUT_OPTION_LABELS[parsed.token as SessionTimeoutPreset];
  const noun =
    parsed.unit === 'minutes'
      ? 'minute'
      : parsed.unit === 'hours'
        ? 'hour'
        : 'day';
  return parsed.amount === 1 ? `1 ${noun}` : `${parsed.amount} ${noun}s`;
}

export function sessionTimeoutChangedAnalytics(
  previousToken: string,
  nextToken: string
) {
  const previous = parseSessionTimeout(previousToken);
  const next = parseSessionTimeout(nextToken);
  return {
    previousValue: previous?.minutes ?? null,
    newValue: next?.minutes ?? null,
    isCustom: next?.isCustom ?? false,
    unit: next?.unit ?? null,
  };
}

export type CustomTimeoutValidation =
  | { ok: true; token: string }
  | { ok: false; errorType: SessionTimeoutErrorType; message: string };

export function validateCustomTimeout(
  amountRaw: string,
  unit: SessionTimeoutUnit
): CustomTimeoutValidation {
  const trimmed = amountRaw.trim();
  if (trimmed === '') {
    return { ok: false, errorType: 'empty', message: 'Enter a number.' };
  }
  if (!/^\d+$/.test(trimmed)) {
    return {
      ok: false,
      errorType: 'not_whole',
      message: 'Enter a whole number greater than 0.',
    };
  }

  const amount = Number(trimmed);
  if (!Number.isSafeInteger(amount) || amount <= 0) {
    return {
      ok: false,
      errorType: 'not_whole',
      message: 'Enter a whole number greater than 0.',
    };
  }

  const factor =
    unit === 'minutes'
      ? 1
      : unit === 'hours'
        ? MINUTES_PER_HOUR
        : MINUTES_PER_DAY;
  if (amount > Math.floor(Number.MAX_SAFE_INTEGER / factor)) {
    return {
      ok: false,
      errorType: 'above_max',
      message: 'Enter 30 days or less.',
    };
  }

  const minutes = amount * factor;
  if (minutes < MIN_SESSION_TIMEOUT_MINUTES) {
    return {
      ok: false,
      errorType: 'below_min',
      message: 'Enter at least 30 minutes.',
    };
  }
  if (minutes > MAX_SESSION_TIMEOUT_MINUTES) {
    return {
      ok: false,
      errorType: 'above_max',
      message: 'Enter 30 days or less.',
    };
  }

  return { ok: true, token: minutesToCanonicalToken(minutes) };
}

export const sessionTimeoutSchema = z.string().transform((value, ctx) => {
  const canonical = canonicalizeSessionTimeout(value);
  if (!canonical) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid session timeout',
    });
    return z.NEVER;
  }
  return canonical;
});

/** Accepts a preset or custom token and stores the canonical form. Omit to leave unchanged. */
export const canonicalSessionTimeoutInput = sessionTimeoutSchema.optional();
